import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BsArrowLeft, BsPaperclip, BsDownload, BsTrash, BsLock } from 'react-icons/bs';
import { apiFetch, parseJsonSafe } from '../utils/api';
import { StatusPill, PriorityPill } from './SupportTicketsPage';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'esperando_cliente', label: 'Esperando cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' }
];
const PRIORITY_OPTIONS = [
  { value: 'baja', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' }
];

const fmtDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('es-ES');
};

const SupportTicketDetailPage = () => {
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/support/${id}`, { headers: authH });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar el ticket');
      setTicket(data);
    } catch (err) {
      setError(err.message);
      setTicket(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/users', { headers: authH });
        const data = await parseJsonSafe(res);
        if (res.ok && Array.isArray(data)) setUsers(data);
      } catch (_e) { /* sin usuarios */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchTicket = async (fields) => {
    try {
      const res = await apiFetch(`/api/support/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify(fields)
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() && !files.length) return;
    setSending(true);
    setError('');
    try {
      let messageId = null;
      // Si hay adjuntos, SIEMPRE creamos un mensaje (aunque no haya texto): así el
      // adjunto queda ligado a un mensaje —heredando su visibilidad interna/pública—,
      // se actualiza el ticket y, si no es interno, se notifica al cliente.
      const body = reply.trim() || (files.length ? '📎 Archivo adjunto' : '');
      if (body) {
        const res = await apiFetch(`/api/support/${id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authH },
          body: JSON.stringify({ body, is_internal: isInternal })
        });
        const data = await parseJsonSafe(res);
        if (!res.ok) throw new Error(data.message || 'No se pudo enviar');
        messageId = data.id;
      }
      const failed = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        if (messageId) fd.append('message_id', messageId);
        const up = await apiFetch(`/api/support/${id}/attachments`, { method: 'POST', headers: authH, body: fd });
        if (!up.ok) failed.push(file.name);
      }
      if (failed.length) {
        // No vaciamos los adjuntos fallidos para que el usuario pueda reintentar.
        setError(`No se pudieron subir estos archivos: ${failed.join(', ')}`);
        setFiles((prev) => prev.filter((f) => failed.includes(f.name)));
        setReply('');
      } else {
        setReply('');
        setIsInternal(false);
        setFiles([]);
      }
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await apiFetch(`/api/support/attachments/${att.id}/download`, { headers: authH });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* noop */ }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`¿Eliminar "${att.filename}"?`)) return;
    await apiFetch(`/api/support/attachments/${att.id}`, { method: 'DELETE', headers: authH });
    load();
  };

  if (loading) return <div className="text-muted">Cargando…</div>;
  if (error && !ticket) return (
    <div>
      <Link to="/soporte" className="btn btn-link px-0"><BsArrowLeft /> Volver</Link>
      <div className="alert alert-danger">{error}</div>
    </div>
  );
  if (!ticket) return null;

  const attsFor = (messageId) => (ticket.attachments || []).filter((a) => a.message_id === messageId);
  const orphanAtts = (ticket.attachments || []).filter((a) => !a.message_id);

  return (
    <div>
      <Link to="/soporte" className="btn btn-link px-0 mb-2"><BsArrowLeft /> Volver a soporte</Link>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h4 className="mb-1">#{ticket.id} · {ticket.subject}</h4>
              <div className="text-muted small">
                Cliente: {ticket.client_id ? <Link to={`/clientes/${ticket.client_id}`}>{ticket.client_name || `Cliente #${ticket.client_id}`}</Link> : <strong>{ticket.client_name || '-'}</strong>}
                {ticket.client_email ? ` · ${ticket.client_email}` : ''} · Abierto el {fmtDateTime(ticket.created_at)}
              </div>
            </div>
            <div className="d-flex gap-2">
              <StatusPill status={ticket.status} />
              <PriorityPill priority={ticket.priority} />
            </div>
          </div>

          <div className="row g-2 mt-2">
            <div className="col-sm-4">
              <label className="form-label small mb-1">Estado</label>
              <select className="form-select form-select-sm" value={ticket.status} onChange={(e) => patchTicket({ status: e.target.value })}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-sm-4">
              <label className="form-label small mb-1">Prioridad</label>
              <select className="form-select form-select-sm" value={ticket.priority} onChange={(e) => patchTicket({ priority: e.target.value })}>
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-sm-4">
              <label className="form-label small mb-1">Asignado a</label>
              <select
                className="form-select form-select-sm"
                value={ticket.assigned_to_user_id || ''}
                onChange={(e) => patchTicket({ assigned_to_user_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Sin asignar</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {orphanAtts.length > 0 && (
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="small text-muted mb-1"><BsPaperclip /> Adjuntos del ticket</div>
            {orphanAtts.map((a) => (
              <AttachmentRow key={a.id} att={a} onDownload={downloadAttachment} onDelete={role === 'admin' ? deleteAttachment : null} />
            ))}
          </div>
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(ticket.messages || []).map((m) => {
            if (m.sender_type === 'sistema') {
              return <div key={m.id} className="text-center text-muted small">— {m.body} ({fmtDateTime(m.created_at)}) —</div>;
            }
            const mine = m.sender_type === 'equipo';
            const bg = m.is_internal ? '#fff3cd' : (mine ? '#e7f1ff' : '#f1f3f5');
            return (
              <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                <div style={{ background: bg, borderRadius: 12, padding: '10px 14px', border: '1px solid rgba(0,0,0,.06)' }}>
                  <div className="small mb-1" style={{ color: '#6c757d' }}>
                    {m.is_internal && <span className="badge bg-warning text-dark me-1"><BsLock size={11} /> Nota interna</span>}
                    <strong>{mine ? (m.author_name || 'Equipo') : (m.author_name || 'Cliente')}</strong> · {fmtDateTime(m.created_at)}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                  {attsFor(m.id).map((a) => (
                    <AttachmentRow key={a.id} att={a} onDownload={downloadAttachment} onDelete={role === 'admin' ? deleteAttachment : null} />
                  ))}
                </div>
              </div>
            );
          })}
          {!(ticket.messages || []).length && <div className="text-muted text-center">Sin mensajes todavía.</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <textarea
            className="form-control mb-2"
            rows="3"
            placeholder={isInternal ? 'Nota interna (no visible para el cliente)…' : 'Escribe una respuesta al cliente…'}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          {files.length > 0 && (
            <div className="mb-2 small">
              {files.map((f, i) => (
                <span key={i} className="badge bg-light text-dark border me-1">
                  {f.name} <button className="btn btn-sm btn-link p-0 text-danger" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <label className="btn btn-outline-secondary btn-sm mb-0">
                <BsPaperclip /> Adjuntar
                <input type="file" multiple hidden onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)])} />
              </label>
              <div className="form-check mb-0">
                <input className="form-check-input" type="checkbox" id="internalNote" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                <label className="form-check-label small" htmlFor="internalNote">Nota interna</label>
              </div>
            </div>
            <button className="btn btn-primary" onClick={sendReply} disabled={sending || (!reply.trim() && !files.length)}>
              {sending ? 'Enviando…' : (isInternal ? 'Guardar nota' : 'Responder')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AttachmentRow = ({ att, onDownload, onDelete }) => (
  <div className="d-flex align-items-center gap-2 mt-1 small">
    <BsPaperclip className="text-muted" />
    <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => onDownload(att)}>
      {att.filename} <BsDownload className="ms-1" />
    </button>
    {onDelete && <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => onDelete(att)}><BsTrash /></button>}
  </div>
);

export default SupportTicketDetailPage;
